import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { 
  CalendarDays, 
  ArrowRight, 
  Users, 
  Plus, 
  CheckCircle, 
  Clock, 
  ChevronRight,
  LayoutGrid,
  FileText,
  AlertCircle
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format, parseISO } from "date-fns";

// Interface for templates
interface Template {
  template_id: string;
  name: string;
  description: string;
  icon?: string;
  background_class?: string;
  default_cadence?: string;
}

// Interface for check-in counts
interface CheckInCounts {
  total: number;
  active: number;
  draft: number;
}

// Interface for check-in data
interface CheckInData {
  check_in_id: string;
  name: string;
  slug: string;
  cutoff_time?: string;
  timezone?: string;
}

// Interface for reports
interface Report {
  report_id: string;
  check_in_id: string;
  check_in_name: string;
  report_date: string;
  status: 'upcoming' | 'active' | 'completed';
  cutoff_time?: string;
  timezone?: string;
  participation_rate?: number;
  check_in_slug?: string;
}

// Function to capitalize first letter
const capitalizeFirstLetter = (string: string): string => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Helper function to format timezone into abbreviated form
const formatTimezone = (timezone: string): string => {
  try {
    const date = new Date();
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'short',
      hour: 'numeric',
    });
    const tzMatch = formatter.format(date).match(/[A-Z]{3,4}$/);
    return tzMatch ? tzMatch[0] : timezone;
  } catch (error) {
    return timezone;
  }
};

export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [checkInCounts, setCheckInCounts] = useState<CheckInCounts>({ 
    total: 0, 
    active: 0, 
    draft: 0 
  });
  const [recentReports, setRecentReports] = useState<Report[]>([]);
  const [upcomingReports, setUpcomingReports] = useState<Report[]>([]);
  
  // Get first name from user's full name or email
  const firstName = user?.user_metadata?.full_name 
    ? capitalizeFirstLetter(user.user_metadata.full_name.split(' ')[0])
    : user?.email ? capitalizeFirstLetter(user.email.split('@')[0]) : 'There';
  
  const currentDate = new Date();
  
  // Fetch data from Supabase
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      
      try {
        // Fetch templates
        const { data: templatesData, error: templatesError } = await supabase
          .from('templates')
          .select('*')
          .order('name');
        
        if (templatesError) throw templatesError;
        setTemplates(templatesData || []);
        
        if (user?.id) {
          // Get check-in counts
          const [totalResult, activeResult, draftResult] = await Promise.all([
            supabase
              .from('check_ins')
              .select('check_in_id', { count: 'exact' })
              .eq('created_by_user_id', user.id),
            supabase
              .from('check_ins')
              .select('check_in_id', { count: 'exact' })
              .eq('created_by_user_id', user.id)
              .eq('first_setup_completed', true),
            supabase
              .from('check_ins')
              .select('check_in_id', { count: 'exact' })
              .eq('created_by_user_id', user.id)
              .eq('first_setup_completed', false)
          ]);
          
          if (totalResult.error || activeResult.error || draftResult.error) {
            throw totalResult.error || activeResult.error || draftResult.error;
          }
          
          setCheckInCounts({
            total: totalResult.data?.length || 0,
            active: activeResult.data?.length || 0,
            draft: draftResult.data?.length || 0
          });
          
          // Fetch active check-ins for reports
          const { data: activeCheckIns, error: checkInsError } = await supabase
            .from('check_ins')
            .select('check_in_id, name, slug, cutoff_time, timezone')
            .eq('created_by_user_id', user.id)
            .eq('first_setup_completed', true)
            .eq('is_active', true);
          
          if (checkInsError) throw checkInsError;
          
          if (activeCheckIns && activeCheckIns.length > 0) {
            // Create a map of check-in IDs to data for later use
            const checkInMap = new Map<string, CheckInData>();
            activeCheckIns.forEach(ci => {
              checkInMap.set(ci.check_in_id, {
                check_in_id: ci.check_in_id,
                name: ci.name,
                slug: ci.slug,
                cutoff_time: ci.cutoff_time,
                timezone: ci.timezone
              });
            });
            
            // Only proceed with report queries if we have active check-ins
            if (activeCheckIns.length > 0) {
              // Get recent reports (completed in the last 14 days)
              const twoWeeksAgo = new Date();
              twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
              
              try {
                const { data: recentReportsData, error: recentError } = await supabase
                  .from('reports')
                  .select('report_id, check_in_id, report_date')
                  .in('check_in_id', activeCheckIns.map(ci => ci.check_in_id))
                  .gt('report_date', twoWeeksAgo.toISOString())
                  .lt('report_date', new Date().toISOString())
                  .order('report_date', { ascending: false })
                  .limit(5);
                
                if (recentError) throw recentError;
                
                // Fetch participation data for recent reports
                const recentReportsWithParticipation = await Promise.all(
                  (recentReportsData || []).map(async (report) => {
                    // Get total employees for this check-in
                    const { data: employees, error: employeesError } = await supabase
                      .from('check_in_employees')
                      .select('employee_id', { count: 'exact' })
                      .eq('check_in_id', report.check_in_id);
                    
                    // Get responses for this report
                    const reportDate = new Date(report.report_date);
                    const nextDay = new Date(reportDate);
                    nextDay.setDate(nextDay.getDate() + 1);
                    
                    const { data: responses, error: responsesError } = await supabase
                      .from('check_in_responses')
                      .select('response_id', { count: 'exact' })
                      .eq('check_in_id', report.check_in_id)
                      .gte('submitted_at', reportDate.toISOString())
                      .lt('submitted_at', nextDay.toISOString());
                    
                    if (employeesError || responsesError) {
                      console.error('Error fetching participation data:', employeesError || responsesError);
                    }
                    
                    const totalEmployees = employees?.length || 0;
                    const responseCount = responses?.length || 0;
                    const participationRate = totalEmployees > 0 
                      ? Math.round((responseCount / totalEmployees) * 100) 
                      : 0;
                    
                    const checkInData = checkInMap.get(report.check_in_id);
                    
                    return {
                      report_id: report.report_id,
                      check_in_id: report.check_in_id,
                      check_in_name: checkInData?.name || 'Unnamed Check-in',
                      report_date: report.report_date,
                      cutoff_time: checkInData?.cutoff_time,
                      timezone: checkInData?.timezone,
                      participation_rate: participationRate,
                      status: 'completed' as const,
                      check_in_slug: checkInData?.slug
                    };
                  })
                );
                
                setRecentReports(recentReportsWithParticipation);
                
              } catch (reportError) {
                console.error('Error fetching recent reports:', reportError);
              }
              
              // Get upcoming reports (next 14 days)
              const twoWeeksFromNow = new Date();
              twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
              
              try {
                const { data: upcomingReportsData, error: upcomingError } = await supabase
                  .from('reports')
                  .select('report_id, check_in_id, report_date')
                  .in('check_in_id', activeCheckIns.map(ci => ci.check_in_id))
                  .gt('report_date', new Date().toISOString())
                  .lt('report_date', twoWeeksFromNow.toISOString())
                  .order('report_date', { ascending: true })
                  .limit(5);
                
                if (upcomingError) throw upcomingError;
                
                // Format upcoming reports using the check-in map
                setUpcomingReports(
                  upcomingReportsData?.map(report => {
                    const checkInData = checkInMap.get(report.check_in_id);
                    return {
                      ...report,
                      check_in_name: checkInData?.name || 'Unnamed Check-in',
                      cutoff_time: checkInData?.cutoff_time,
                      timezone: checkInData?.timezone,
                      check_in_slug: checkInData?.slug,
                      status: 'upcoming' as const
                    };
                  }) || []
                );
              } catch (reportError) {
                console.error('Error fetching upcoming reports:', reportError);
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [user?.id]);
  
  // Navigate to Create New Check-In page
  const handleCreateCheckIn = () => {
    navigate('/check-ins/new');
  };
  
  // Navigate to Check-Ins list page
  const handleViewCheckIns = () => {
    navigate('/check-ins');
  };
  
  // Navigate to report details
  const handleViewReport = (slug: string, reportId: string) => {
    navigate(`/check-ins/report/${slug}?reportId=${reportId}`);
  };
  
  // Navigate to check-in details
  const handleViewCheckIn = (slug: string) => {
    navigate(`/check-ins/${slug}`);
  };
  
  // Template card background colors
  const templateColors = [
    "bg-blue-50 dark:bg-blue-950/30",
    "bg-purple-50 dark:bg-purple-950/30",
    "bg-emerald-50 dark:bg-emerald-950/30",
    "bg-amber-50 dark:bg-amber-950/30",
    "bg-pink-50 dark:bg-pink-950/30",
    "bg-cyan-50 dark:bg-cyan-950/30"
  ];
  
  // Template card icons
  const getTemplateIcon = (template: Template, index: number) => {
    if (template.icon === 'check') return <CheckCircle className="h-5 w-5 text-emerald-500" />;
    if (template.icon === 'clock') return <Clock className="h-5 w-5 text-blue-500" />;
    
    // Cycle through different colors if no specific icon
    const iconColors = ['text-blue-500', 'text-purple-500', 'text-emerald-500', 'text-amber-500', 'text-pink-500', 'text-cyan-500'];
    const colorClass = iconColors[index % iconColors.length];
    
    return <LayoutGrid className={`h-5 w-5 ${colorClass}`} />;
  };
  
  const getReportStatusBadge = (report: Report) => {
    if (report.status === 'completed') {
      return (
        <Badge 
          variant={
            report.participation_rate && report.participation_rate > 66 
              ? 'default' 
              : report.participation_rate && report.participation_rate > 33 
                ? 'outline' 
                : 'destructive'
          }
          className="ml-auto"
        >
          {report.participation_rate || 0}% Participation
        </Badge>
      );
    }
    
    const daysUntil = Math.ceil((new Date(report.report_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    return (
      <Badge variant="outline" className="ml-auto bg-accent">
        {daysUntil === 0 ? 'Today' : daysUntil === 1 ? 'Tomorrow' : `In ${daysUntil} days`}
      </Badge>
    );
  };

  // Format cutoff time with timezone
  const formatCutoffTime = (report: Report) => {
    if (!report.cutoff_time || !report.timezone) return null;
    
    try {
      const time = format(parseISO(`2000-01-01T${report.cutoff_time}`), 'h:mm a');
      const tz = formatTimezone(report.timezone);
      return `${time} ${tz}`;
    } catch (error) {
      return null;
    }
  };
  
  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Greeting Header */}
      <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3 sm:gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Hello, {firstName}</h1>
          <p className="text-sm sm:text-base md:text-lg text-muted-foreground mt-1 sm:mt-1.5">
            Welcome to your dashboard
          </p>
        </div>
        <div className="flex items-center gap-2 text-muted-foreground bg-accent/50 px-3 py-1.5 rounded-lg self-start">
          <CalendarDays className="h-4 w-4" />
          <span className="text-sm font-medium">{formatDate(currentDate)}</span>
        </div>
      </div>
      
      <Separator className="my-4 sm:my-6" />
      
      {/* Check-Ins Overview Card */}
      <Card className="overflow-hidden border-accent/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-xl">Your Check-Ins</CardTitle>
          <CardDescription>
            View and manage your team's check-ins
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex gap-8 px-6 py-8">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-8 items-start sm:items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <Users className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold">{checkInCounts.active}</h3>
                  <p className="text-muted-foreground">Active check-ins</p>
                </div>
              </div>
              <div className="flex gap-4">
                <Button 
                  variant="outline" 
                  onClick={handleViewCheckIns}
                  className="gap-2"
                >
                  View Check-Ins
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button onClick={handleCreateCheckIn} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Create New
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Recent and Upcoming Reports Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Reports */}
        <Card className="border-accent/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium flex items-center">
              <FileText className="h-5 w-5 mr-2 text-primary/70" />
              Recent Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between items-center py-2">
                    <Skeleton className="h-5 w-[180px]" />
                    <Skeleton className="h-5 w-[80px]" />
                  </div>
                ))}
              </div>
            ) : recentReports.length > 0 ? (
              <div className="space-y-1">
                {recentReports.map((report) => (
                  <div 
                    key={report.report_id}
                    onClick={() => report.check_in_slug && handleViewReport(report.check_in_slug, report.report_id)}
                    className="flex items-center justify-between py-2 px-2 hover:bg-accent/40 rounded-md cursor-pointer transition-colors"
                  >
                    <div>
                      <p className="font-medium">{report.check_in_name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(report.report_date), 'MMM d, yyyy')}
                        </p>
                        {formatCutoffTime(report) && (
                          <p className="text-xs text-muted-foreground/80 px-1.5 py-0.5 bg-muted rounded">
                            {formatCutoffTime(report)}
                          </p>
                        )}
                      </div>
                    </div>
                    {getReportStatusBadge(report)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <FileText className="h-10 w-10 text-muted-foreground/40 mb-2" />
                <p className="text-muted-foreground">No recent reports</p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Upcoming Reports */}
        <Card className="border-accent/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-medium flex items-center">
              <CalendarDays className="h-5 w-5 mr-2 text-primary/70" />
              Upcoming Reports
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex justify-between items-center py-2">
                    <Skeleton className="h-5 w-[180px]" />
                    <Skeleton className="h-5 w-[80px]" />
                  </div>
                ))}
              </div>
            ) : upcomingReports.length > 0 ? (
              <div className="space-y-1">
                {upcomingReports.map((report) => (
                  <div 
                    key={report.report_id}
                    className={
                      "flex items-center justify-between py-2 px-2 rounded-md transition-colors " +
                      (report.check_in_slug ? "hover:bg-accent/40 cursor-pointer" : "opacity-60 cursor-not-allowed")
                    }
                    onClick={report.check_in_slug ? () => handleViewCheckIn(report.check_in_slug!) : undefined}
                  >
                    <div>
                      <p className="font-medium">{report.check_in_name}</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-muted-foreground">
                          {format(parseISO(report.report_date), 'MMM d, yyyy')}
                        </p>
                        {formatCutoffTime(report) && (
                          <p className="text-xs text-muted-foreground/80 px-1.5 py-0.5 bg-muted rounded">
                            {formatCutoffTime(report)}
                          </p>
                        )}
                      </div>
                    </div>
                    {getReportStatusBadge(report)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <AlertCircle className="h-10 w-10 text-muted-foreground/40 mb-2" />
                <p className="text-muted-foreground">No upcoming reports</p>
              </div>
            )}
          </CardContent>
          {upcomingReports.length > 0 && (
            <CardFooter className="pt-0">
              <Button variant="ghost" size="sm" className="ml-auto" onClick={handleViewCheckIns}>
                View all check-ins
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </CardFooter>
          )}
        </Card>
      </div>
      
      {/* Templates Section */}
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold flex items-center">
            <LayoutGrid className="h-5 w-5 mr-2 text-primary/70" />
            Templates
          </h2>
          <Button variant="ghost" size="sm" onClick={handleCreateCheckIn}>
            View all templates
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
        
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="overflow-hidden">
                <div className="p-6">
                  <Skeleton className="h-6 w-[120px] mb-2" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4 mt-1" />
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.length > 0 ? templates.slice(0, 6).map((template, index) => (
              <Card 
                key={template.template_id}
                className={cn(
                  "overflow-hidden cursor-pointer transition-all hover:shadow-md border-0",
                  template.background_class || templateColors[index % templateColors.length]
                )}
                onClick={() => {
                  navigate('/check-ins/new', { state: { templateId: template.template_id } });
                }}
              >
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <div className="bg-white dark:bg-gray-800 p-2 rounded-full shadow-sm">
                      {getTemplateIcon(template, index)}
                    </div>
                    <Badge variant="outline" className="bg-white/50 dark:bg-gray-800/50">
                      {template.default_cadence || 'Weekly'}
                    </Badge>
                  </div>
                  <h3 className="font-semibold text-lg mt-3">{template.name}</h3>
                  <p className="text-sm text-muted-foreground line-clamp-2 mt-1.5">
                    {template.description || "No description provided"}
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full mt-4 justify-between items-center bg-white/50 dark:bg-gray-800/30 hover:bg-white/80 dark:hover:bg-gray-800/50"
                  >
                    Use template
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            )) : (
              <div className="col-span-full flex justify-center p-8 border rounded-lg border-dashed">
                <p className="text-muted-foreground">No templates available</p>
              </div>
            )}
          </div>
        )}
        
        {templates.length > 6 && (
          <div className="flex justify-center mt-6">
            <Button variant="outline" onClick={handleCreateCheckIn}>
              View all {templates.length} templates
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}